import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../../../environments/environment';
import {ResearchPaperCreateDTO, ResearchPaperDTO, ResearchPaperManuelScreeningDTO} from '../../dto/research-paper';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ScreeningProcessDTO} from '../../dto/screening-process';

@Injectable({
  providedIn: 'root',
})
export class ResearchPaperService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly apiUrl = environment.apiBaseUrl + '/project';

  listPapers(projectId: number): Observable<ResearchPaperDTO[]> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    return this.http.get<ResearchPaperDTO[]>(`${this.apiUrl}/${projectId}/research/paper`);
  }

  createPaperWithoutFile(projectId: number, paper: ResearchPaperCreateDTO): Observable<ResearchPaperDTO> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    return this.http.post<ResearchPaperDTO>(`${this.apiUrl}/${projectId}/research/paper`, paper);
  }

  createPaper(projectId: number, paper: ResearchPaperCreateDTO, file?: File): Observable<ResearchPaperDTO> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    const formData: FormData = new FormData();
    if (file) {
      formData.append('pdf', file, file.name);
    }
    formData.append('paper', JSON.stringify(paper));

    return this.http.post<ResearchPaperDTO>(`${this.apiUrl}/${projectId}/research/paper-with-file`, formData);
  }

  createBulkPaperCSV(projectId: number, csv: File, pdfs?: File[]): Observable<ResearchPaperDTO[]> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    const formData: FormData = new FormData();
    formData.append('csv', csv, csv.name);
    if (pdfs && pdfs.length > 0) {
      Array.from(pdfs).forEach((pdf) => {
        formData.append('pdfs', pdf, pdf.name);
      });
    }

    return this.http.post<ResearchPaperDTO[]>(`${this.apiUrl}/${projectId}/research/papers/batch`, formData);
  }


  retrievePaper(projectId: number, id: number): Observable<ResearchPaperDTO> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    if (!id) {
      throw new Error('Id is required');
    }
    return this.http.get<ResearchPaperDTO>(`${this.apiUrl}/${projectId}/research/paper/${id}`);
  }

  updatePaper(projectId: number, id: number, paper: ResearchPaperDTO): Observable<ResearchPaperDTO> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    if (!id) {
      throw new Error('Id is required');
    }
    return this.http.put<ResearchPaperDTO>(`${this.apiUrl}/${projectId}/research/paper/${id}`, paper);
  }

  updateManuelScreeningResearchPaper(projectId: number, processId: number, result: ResearchPaperManuelScreeningDTO): Observable<ResearchPaperDTO> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    if (!processId) {
      throw new Error('processId is required');
    }
    return this.http.put<ResearchPaperDTO>(`${this.apiUrl}/${projectId}/screenings/process/${processId}`, result);
  }

  createManuelScreeningResearchPaper(projectId: number, paperId: number, result: ResearchPaperManuelScreeningDTO): Observable<ScreeningProcessDTO> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    if (!paperId) {
      throw new Error('paperId is required');
    }
    return this.http.post<ScreeningProcessDTO>(`${this.apiUrl}/${projectId}/screenings/paper/${paperId}/override`, result);
  }

  deletePaper(projectId: number, id: number): Observable<void> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    if (!id) {
      throw new Error('Id is required');
    }
    return this.http.delete<void>(`${this.apiUrl}/${projectId}/research/paper/${id}`, {observe: 'response'})
      .pipe(map(response => {
        if (response.status === 200) {
          return;
        }
        throw new Error('Delete failed');
      }));
  }

  deleteAllPaper(projectId: number): Observable<void> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    return this.http.delete<void>(`${this.apiUrl}/${projectId}/research/paper`, {observe: 'response'})
      .pipe(map(response => {
        if (response.status === 200) {
          return;
        }
        throw new Error('Delete failed');
      }));
  }

  queryPapers(projectId: number): Observable<ResearchPaperDTO[]> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    return this.http.get<ResearchPaperDTO[]>(`${this.apiUrl}/${projectId}/research/query`);
  }


  uploadPdf(projectId: number, id: number, file: File): Observable<ResearchPaperDTO> {
    if (!projectId) {
      throw new Error('ProjectId is required');
    }
    if (!id) {
      throw new Error('Id is required');
    }
    const formData: FormData = new FormData();
    formData.append('pdf', file, file.name);
    return this.http.post<ResearchPaperDTO>(`${this.apiUrl}/${projectId}/research/paper/${id}/upload`, formData);
  }

}

